import React, { useCallback, useEffect, useMemo, useState, } from 'react';
import { Dimensions, Keyboard, Platform, Pressable, StyleSheet, View, } from 'react-native';
import Animated, { interpolate, useAnimatedStyle, useSharedValue, } from 'react-native-reanimated';
import { KeyboardController } from 'react-native-keyboard-controller';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import useIsTablet from 'hooks/useIsTablet';
import ModalConfirmationContent from './ModalConfirmationContent';
import ModalHandle from './ModalHandle';
import { ModalScrollOffsetContext, ModalScrollView } from './ModalScrollables';
import ModalSheet from './ModalSheet';
import Separator from './Separator';
import Text from './Text';
import { rem } from './values';
const strings = { error: 'Error', tryAgain: 'Try again', ok: 'Okay' };
const Modal = ({ top }) => {
    const { height, width } = useAppFrameDimensions();
    const isTablet = useIsTablet();
    const [state, setState] = useState({
        visible: false,
        content: null,
        title: '',
    });
    const styles = useStyles(styleSet);
    const close = useCallback(() => {
        setState((state) => ({
            ...state,
            visible: false,
        }));
    }, []);
    const onBackdropPress = useCallback(() => {
        state.options?.onBackdropPress?.();
        close();
    }, [close, state.options]);
    const onSwipeComplete = useCallback(() => {
        state.options?.onSwipeComplete?.();
        close();
    }, [close, state.options]);
    const onBackButtonPress = useCallback(() => {
        if (state.options?.nonDismissable) {
            // do not allow to close modal in this case
        }
        else {
            state.options?.onAndroidBackButtonPress?.();
            close();
        }
    }, [state.options, close]);
    const onHideEvent = useCallback((key) => {
        if (Modal.visibility.has(key)) {
            Modal.visibility.delete(key);
        }
        if (Modal.globalDismissListeners) {
            Modal.globalDismissListeners.forEach((listener) => listener());
        }
    }, []);
    const animatedScrollOffset = useSharedValue(0);
    const cleanup = useCallback((key) => {
        onHideEvent(key);
        if (Modal.onDidHideCallbacks) {
            const callback = Modal.onDidHideCallbacks[top ? 'top' : 'bottom'];
            if (typeof callback === 'function') {
                callback();
                Modal.onDidHideCallbacks[top ? 'top' : 'bottom'] = undefined;
            }
        }
        animatedScrollOffset.value = 0;
    }, [animatedScrollOffset, onHideEvent, top]);
    useEffect(() => {
        if (Modal.setStateRefs === undefined)
            Modal.setStateRefs = {};
        Modal.setStateRefs[top ? 'top' : 'bottom'] = setState;
    }, [top]);
    const onPress = useCallback(() => {
        if (state.options?.onPress) {
            if (Modal.onDidHideCallbacks === undefined) {
                Modal.onDidHideCallbacks = {};
            }
            Modal.onDidHideCallbacks[top ? 'top' : 'bottom'] = state.options.onPress;
        }
        close();
    }, [state.options?.onPress, close, top]);
    const onPressCancel = useCallback(() => {
        if (state.options?.onPressCancel) {
            if (Modal.onDidHideCallbacks === undefined) {
                Modal.onDidHideCallbacks = {};
            }
            Modal.onDidHideCallbacks[top ? 'top' : 'bottom'] =
                state.options.onPressCancel;
        }
        close();
    }, [state.options?.onPressCancel, close, top]);
    const scrollValueToUse = useMemo(() => state.options?.scrollable?.scrollValue
        ? state.options?.scrollable?.scrollValue
        : animatedScrollOffset, [animatedScrollOffset, state.options?.scrollable?.scrollValue]);
    const separator = useAnimatedStyle(() => ({
        opacity: interpolate(scrollValueToUse.value, [0, 40], [0, 1]),
    }), [scrollValueToUse]);
    const paddingBottom = useMarginBottom('paddingBottom');
    const isConfirmation = !!state.options && 'showConfirmation' in state.options;
    const isError = !!state.options && 'showError' in state.options;
    const fullscreen = !isConfirmation && !isError && !!state.options?.fullscreen;
    const isTabletCard = isTablet && !state.options?.fullscreen;
    let visibilityKeyOnHide;
    let body;
    if (isConfirmation && state.options) {
        visibilityKeyOnHide = visibilityKey('showConfirmation', 'top');
        body = (<ModalConfirmationContent options={state.options} containerStyle={[
                styles.errorContainer,
                isTabletCard && styles.containerTablet,
                !isTablet && paddingBottom,
            ]} onPress={onPress} onPressCancel={onPressCancel} title={state.title} subtitle={state.subtitle}/>);
    }
    else if (isError && state.options) {
        visibilityKeyOnHide = visibilityKey('showError', 'top');
        body = (<ModalConfirmationContent containerStyle={[
                styles.errorContainer,
                isTabletCard && styles.containerTablet,
                !isTablet && paddingBottom,
            ]} onPress={onPress} title={state.title} subtitle={state.subtitle} options={{
                ...state.options,
                confirmButtonTitle: state.options.onPress
                    ? state.options.confirmButtonTitle || strings.tryAgain
                    : strings.ok,
            }}/>);
    }
    else {
        visibilityKeyOnHide = visibilityKey('show', state.options?.top ? 'top' : 'bottom');
        const { title } = state;
        let titleNode;
        if (typeof title === 'string') {
            if (state.title === '') {
                titleNode = null;
            }
            else {
                titleNode = (<Text Text-20 centered style={[
                        styles.title,
                        state.options?.increasedTopPadding && styles.increasedTopPadding,
                        state.options?.scrollable && styles.titleScrollable,
                        state.options?.textStyle,
                    ]}>
            {state.title}
          </Text>);
            }
        }
        else {
            titleNode = title;
        }
        body = (<View style={[
                styles.container,
                isTabletCard && styles.containerTablet,
                state.options?.fullscreen
                    ? {
                        height: Platform.OS === 'ios'
                            ? height
                            : Dimensions.get('screen').height,
                    }
                    : [
                        styles.nonFullscreen,
                        state.options?.darkBg && styles.darkBg,
                        !isTablet && paddingBottom,
                    ],
                state.options?.scrollable && styles.containerScrollable,
                state.options?.containerStyle,
            ]}>
        {titleNode}
        {state.options?.scrollable !== undefined ? (<View style={styles.scrollContainer}>
            {state.options?.scrollable.scrollValue ? (state.content) : (<ModalScrollView style={{ maxHeight: state.options.scrollable.maxHeight }} contentContainerStyle={[
                        {
                            ...(state.options.scrollable.outsideContent
                                ? { paddingBottom: 0 }
                                : paddingBottom),
                        },
                    ]}>
                {state.content}
              </ModalScrollView>)}
            {state.options.scrollable.outsideContent}
            <Animated.View style={[styles.separatorContainer, separator]}>
              <Separator />
            </Animated.View>
          </View>) : (state.content)}
        {!state.options?.hideHandle && !state.options?.fullscreen && (<ModalHandle />)}
      </View>);
    }
    // On a tablet the card sits in the middle of a full-height, transparent
    // sheet, and a press around it counts as a backdrop press.
    if (isTabletCard) {
        body = (<View style={staticStyles.tabletCentre}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onBackdropPress} accessible={false}/>
        <View style={{ width }}>{body}</View>
      </View>);
    }
    return (<ModalSheet visible={state.visible} fade={fullscreen ? !!state.options?.fade : false} fullHeight={fullscreen || isTabletCard} dismissable={!state.options?.nonDismissable || isConfirmation || isError} noBackdrop={state.options?.noBackdrop} onBackdropPress={onBackdropPress} onSwipeComplete={onSwipeComplete} onBackButtonPress={onBackButtonPress} onShow={state.options?.onModalShow} onWillHide={state.options?.onModalWillHide} onHide={() => {
            cleanup(visibilityKeyOnHide);
        }}>
      <ModalScrollOffsetContext.Provider value={scrollValueToUse}>
        {body}
      </ModalScrollOffsetContext.Provider>
    </ModalSheet>);
};
Modal.minHideTime = { top: 0, bottom: 0 };
Modal.visibility = new Set();
Modal.globalDismissListeners = [];
// Keyboard.dismiss() blurs only the input React Native records as focused. When
// that record is stale, the keyboard stays up and covers the sheet's buttons,
// so also ask the OS to drop whatever input is actually focused.
const dismissKeyboard = () => {
    Keyboard.dismiss();
    KeyboardController.dismiss();
};
const visibilityKey = (method, slot) => `${method}-${slot}`;
Modal.isAnyModalOpen = () => Modal.visibility.size > 0;
Modal.addGlobalDismissListener = (callback) => {
    if (!Modal.globalDismissListeners)
        Modal.globalDismissListeners = [];
    Modal.globalDismissListeners.push(callback);
    return () => {
        if (Modal.globalDismissListeners) {
            Modal.globalDismissListeners = Modal.globalDismissListeners.filter((cb) => cb !== callback);
        }
    };
};
Modal.show = (content, options) => {
    if (Modal.setStateRefs) {
        if (options?.onDidHideCallback) {
            if (Modal.onDidHideCallbacks === undefined) {
                Modal.onDidHideCallbacks = {};
            }
            Modal.onDidHideCallbacks[options?.top ? 'top' : 'bottom'] =
                options.onDidHideCallback;
        }
        const setState = Modal.setStateRefs[options?.top ? 'top' : 'bottom'];
        if (setState) {
            const slot = options?.top ? 'top' : 'bottom';
            if (Modal.visibility) {
                Modal.visibility.add(visibilityKey('show', slot));
            }
            setState({
                visible: true,
                title: options?.title ?? '',
                content,
                options,
            });
            if (options?.minShowTime) {
                Modal.minHideTime[options?.top ? 'top' : 'bottom'] =
                    new Date().getTime() + options.minShowTime;
            }
            else {
                Modal.minHideTime[options?.top ? 'top' : 'bottom'] = 0;
            }
        }
    }
};
Modal.showError = (message, onTryAgain, title, buttonText, onDidHideCallback) => {
    dismissKeyboard();
    if (Modal.setStateRefs) {
        if (onDidHideCallback) {
            if (Modal.onDidHideCallbacks === undefined) {
                Modal.onDidHideCallbacks = {};
            }
            Modal.onDidHideCallbacks.top = onDidHideCallback;
        }
        const setState = Modal.setStateRefs.top;
        if (setState) {
            if (Modal.visibility) {
                Modal.visibility.add(visibilityKey('showError', 'top'));
            }
            setState({
                visible: true,
                title: title || strings.error,
                subtitle: message,
                content: null,
                options: {
                    showError: true,
                    onPress: onTryAgain,
                    confirmButtonTitle: buttonText || strings.tryAgain,
                },
            });
        }
    }
};
Modal.showConfirmation = ({ title, subtitle, confirmButtonTitle, cancelButtonTitle, onAndroidBackButtonPress, onConfirm, onPressCancel, onBackdropPress, onSwipeComplete, brandReversedConfirm = false, confirmButtonLeftIcon, }) => {
    dismissKeyboard();
    if (Modal.setStateRefs) {
        const setState = Modal.setStateRefs.top;
        if (setState) {
            if (Modal.visibility) {
                Modal.visibility.add(visibilityKey('showConfirmation', 'top'));
            }
            setState({
                visible: true,
                title,
                subtitle,
                content: null,
                options: {
                    showConfirmation: true,
                    onPress: onConfirm,
                    onPressCancel,
                    confirmButtonTitle,
                    cancelButtonTitle,
                    onBackdropPress,
                    onSwipeComplete,
                    brandReversedConfirm,
                    confirmButtonLeftIcon,
                    onAndroidBackButtonPress,
                },
            });
        }
    }
};
Modal.hide = (onDidHideCallback, options) => {
    if (Modal.setStateRefs) {
        if (onDidHideCallback) {
            if (Modal.onDidHideCallbacks === undefined) {
                Modal.onDidHideCallbacks = {};
            }
            Modal.onDidHideCallbacks[options?.top ? 'top' : 'bottom'] =
                onDidHideCallback;
        }
        const setState = Modal.setStateRefs[options?.top ? 'top' : 'bottom'];
        if (setState) {
            const minHideTime = Modal.minHideTime[options?.top ? 'top' : 'bottom'];
            const now = new Date().getTime();
            if (now < minHideTime) {
                setTimeout(() => {
                    setState((state) => ({ ...state, visible: false }));
                }, minHideTime - now);
            }
            else {
                setState((state) => ({ ...state, visible: false }));
            }
        }
    }
};
const staticStyles = StyleSheet.create({
    tabletCentre: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
});
const styleSet = createStyleSheets((colors) => ({
    container: {
        width: '100%',
    },
    containerTablet: {
        borderRadius: rem(16),
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
    },
    containerScrollable: {
        paddingHorizontal: 0,
        paddingBottom: 0,
    },
    nonFullscreen: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        paddingHorizontal: rem(16),
        paddingBottom: rem(16),
        backgroundColor: colors.cards.onDark,
        overflow: 'hidden',
    },
    errorContainer: {
        width: '100%',
        borderRadius: rem(16),
        paddingHorizontal: rem(16),
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        paddingBottom: rem(30),
        paddingTop: rem(40),
        alignItems: 'center',
        backgroundColor: colors.cards.onDark,
    },
    title: {
        marginTop: rem(40),
        alignSelf: 'center',
        color: colors.text.primary,
        marginHorizontal: rem(16),
    },
    titleScrollable: {
        paddingBottom: rem(16),
    },
    increasedTopPadding: {
        marginTop: rem(60),
    },
    separatorContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
    },
    scrollContainer: {
        overflow: 'hidden',
    },
    darkBg: {
        backgroundColor: colors.background.dark,
    },
}));
export default Modal;
